<template>
  <div class="nc-text tw-grid tw-grid-cols-12 tw-gap-4">
    <template v-if="!isManualModeEnabled">
      <div class="tw-col-span-8">
        <multiple-input-group :is-popup-manageable-by-parent="true" :is-popup-visible-parent="isResultsVisible">
          <template #front-item>
            <nc-input
              autocomplete="on"
              v-model="keyword"
              :is-required="isRequired"
              :label="searchInputLabel"
              :placeholder="searchInputPlaceholder"
              @on-keyup-enter="onSearch()"
            />
          </template>
          <template #group>
            <div v-if="isResultsLoading"><loader /></div>
            <div v-else-if="addressSearchResults && addressSearchResults.length > 0">
              <div
                v-for="(resultItem, index) in addressSearchResults"
                :key="`resultItem-${index}`"
                class="address-search-result-item tw-mb-2 tw-px-3 tw-py-3"
                @click="onClickedResultItem(resultItem)"
              >
                {{ resultItem.addressline1 }}
              </div>
            </div>
            <div v-else-if="!addressSearchResults || addressSearchResults.length === 0">Couldn't find the address</div>
          </template>
        </multiple-input-group>
      </div>
      <div class="custom-find-btn tw-col-span-4 tw-flex tw-flex-col tw-justify-end">
        <nc-button styleClass="tw-h-[60px]" label="Find" @on-click="onSearch()"></nc-button>
      </div>
    </template>
    <div v-else-if="isManualModeEnabled" class="w-full tw-col-span-12">
      <manuel-address v-model:full-address="childModelValue" />
    </div>
    <div v-if="childModelValue" class="tw-col-span-12 tw-mt-2">
      <nc-input v-model="childModelValue.display_name" label="Address" placeholder="Address" />
    </div>
    <div v-if="isAddressModeSwitchingEnabled" class="tw-col-span-12 tw-mt-3 tw-flex tw-flex-row tw-justify-end">
      <span
        v-if="!isManualModeEnabled"
        style="color: var(--nc-ternary-color); font-weight: 100"
        class="nc-link-text"
        @click="toggleManualMode(true)"
      >
        <lazy-library-text text="Can’t find the property?"></lazy-library-text>
      </span>
      <span
        v-else-if="isManualModeEnabled"
        style="color: var(--nc-ternary-color); font-weight: 100"
        class="nc-link-text"
        @click="toggleManualMode(false)"
      >
        <lazy-library-text text="Can’t find the property?"></lazy-library-text>
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import NcInput from '~/components/common/inputs/NcInput.vue';
import MultipleInputGroup from '~/components/common/MultipleInputGroup.vue';
import Loader from '~/components/common/Loader.vue';
import { AddressMixin } from '~/units/core/mixins/addressMixin';
import type { ContactAddress } from '~/units/contacts/types';
import ManuelAddress from '~/components/common/address/ManualAddress.vue';
import NcButton from '~/components/common/inputs/NcButton.vue';
import { useAddressesStore } from '~/units/addresses/store';

type AddressSearchResult = {
  addressline1: string;
  summaryline: string;
};

export default defineNuxtComponent({
  name: 'PostcodeSearcher',
  components: { NcButton, ManuelAddress, Loader, MultipleInputGroup, NcInput },

  mixins: [AddressMixin],

  data(): {
    isManualModeEnabled: boolean;
    keyword: string;
    addressSearchResults: AddressSearchResult[];
    isResultsVisible: boolean;
    isResultsLoading: boolean;
  } {
    return {
      isManualModeEnabled: false,
      keyword: '',

      addressSearchResults: [],

      isResultsVisible: false,
      isResultsLoading: false,
    };
  },

  props: {
    modelValue: {
      required: true,
      type: [Object, null] as PropType<ContactAddress | null>,
    },

    label: {
      required: false,
      type: String,
      default: null,
    },

    isAddressModeSwitchingEnabled: {
      required: false,
      type: Boolean,
      default: true,
    },

    searchInputLabel: {
      required: false,
      type: String,
      default: 'Your Address',
    },

    searchInputPlaceholder: {
      required: false,
      type: String,
      default: 'Postcode',
    },

    isRequired: {
      required: false,
      type: Boolean,
      default: true,
    },
  },

  emits: ['update:modelValue'],

  computed: {
    childModelValue: {
      get(): ContactAddress | null {
        return this.modelValue;
      },
      set(value: ContactAddress | null) {
        this.$emit('update:modelValue', value);
      },
    },
  },

  methods: {
    toggleManualMode(value: boolean) {
      this.isManualModeEnabled = value;
    },

    async onSearch() {
      try {
        if (this.keyword) {
          this.isResultsVisible = true;
          this.isResultsLoading = true;
          const addressesStore = useAddressesStore();
          // temporary keyword shorter if length of it is higher than 10. It needs to be replaced with postcode validator later :)
          const keyword = this.keyword && this.keyword.length < 10 ? this.keyword : this.keyword.substring(0, 10);

          const result = await addressesStore.getAddressesByPostcode(keyword);
          this.addressSearchResults = [...result.data];
        }
      } catch (e) {
        this.addressSearchResults = [];
        this.warningMessage('Please enter valid postcode');
      }
      this.isResultsLoading = false;
    },

    async onClickedResultItem(e: AddressSearchResult) {
      this.isResultsLoading = true;
      const addressesStore = useAddressesStore();
      const { data } = await addressesStore.getGeocodeByFullAddress(e.summaryline);
      this.getAddress({
        addressGoogle: data,
        addressPostCode: e,
      });
      this.isResultsLoading = false;
      this.isResultsVisible = false;
    },
  },

  watch: {
    modelValue: {
      handler(e) {
        if (e) {
          if (e.postcode) {
            // sets postcode to address field when on the fly card data has applied
            this.keyword = e.postcode;
          }
        }
      },
      immediate: true,
    },
  },
});
</script>

<style scoped>
.n-input {
  font-weight: 100;
  font-size: 16px;
  border: 1px solid #aca39a;
}

.n-input input {
  border: none !important;
  padding-left: 30px;
}

.n-input input::placeholder {
  color: var(--input--placeholder);
  font-weight: 100 !important;
}

.nc-link-text:hover {
  cursor: pointer;
  text-decoration: underline;
}

.address-search-result-item {
}

.address-search-result-item:hover {
  background-color: #f8f8f8;
}
</style>
